import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AclAction, AclResource, AclService } from '@lib/acl';
import { View } from '@lib/core';
import { InvoiceDataService } from '../invoice-data.service';
import { InvoiceDetail } from '../detail/types/InvoiceDetail';

@Component({
    selector: 'invoice-create-success',
    standalone: true,
    imports: [CommonModule, TranslateModule, RouterLink],
    templateUrl: './invoice-create-success.component.html',
    styleUrls: ['./invoice-create-success.component.scss'],
})
export class InvoiceCreateSuccessComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly invoiceDataService = inject(InvoiceDataService);
    private readonly aclService = inject(AclService);

    protected readonly View = View;

    protected readonly invoiceNumber = signal<string | null>(null);
    protected readonly invoiceId = signal<string | null>(null);
    protected readonly loadingInvoice = signal<boolean>(true);
    protected readonly loadError = signal<boolean>(false);
    protected readonly invoiceAccessAllowed = signal<boolean>(false);
    protected readonly detailAllowedFromNavigation = signal<boolean | null>(null);

    protected readonly permissionsLoading = signal<boolean>(true);
    protected readonly permissions = signal({
        canViewReceipts: false,
        canViewInvoices: false,
        canViewInvoiceDetail: false,
    });

    protected readonly detailLinkAllowed = computed(
        () => this.permissions().canViewInvoiceDetail && this.invoiceAccessAllowed() && !!this.invoiceId(),
    );

    protected readonly hasAnyLink = computed(
        () =>
            this.permissions().canViewReceipts ||
            this.permissions().canViewInvoices ||
            this.detailLinkAllowed(),
    );

    public ngOnInit(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const id = params.get('id');
            if (!id) {
                this.loadingInvoice.set(false);
                this.loadError.set(true);
                return;
            }

            this.invoiceId.set(id);
            // Use navigation state invoice number when available to avoid extra calls for unauthorized users
            const stateInvoiceNumber = history.state?.invoiceNumber as string | undefined;
            if (stateInvoiceNumber) {
                this.invoiceNumber.set(stateInvoiceNumber);
                this.loadingInvoice.set(false);
            }

            if (typeof history.state?.detailAllowed === 'boolean') {
                this.detailAllowedFromNavigation.set(history.state.detailAllowed);
                if (!history.state.detailAllowed) {
                    this.invoiceAccessAllowed.set(false);
                    this.loadingInvoice.set(false);
                }
            }

            this.loadPermissions();
        });
    }

    private fetchInvoiceDetails(id: string): void {
        this.loadingInvoice.set(true);
        this.loadError.set(false);

        this.invoiceDataService
            .getStandaloneInvoice(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (invoice: InvoiceDetail) => {
                    this.invoiceNumber.set(invoice.invoiceNumber);
                    this.invoiceAccessAllowed.set(true);
                    this.loadingInvoice.set(false);
                },
                error: () => {
                    this.loadingInvoice.set(false);
                    this.loadError.set(true);
                    this.invoiceAccessAllowed.set(false);
                },
            });
    }

    private loadPermissions(): void {
        this.permissionsLoading.set(true);

        this.aclService
            .getPermission([
                { resource: AclResource.receiptList, action: AclAction.SHOW },
                { resource: AclResource.invoiceList, action: AclAction.SHOW },
                { resource: AclResource.invoiceDetail, action: AclAction.SHOW },
            ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (responses) => {
                    this.permissions.set({
                        canViewReceipts:
                            responses.find((response) => response.request.resource === AclResource.receiptList)?.allow ??
                            false,
                        canViewInvoices:
                            responses.find((response) => response.request.resource === AclResource.invoiceList)?.allow ??
                            false,
                        canViewInvoiceDetail:
                            responses.find((response) => response.request.resource === AclResource.invoiceDetail)
                                ?.allow ?? false,
                    });

                    const canViewDetail = this.permissions().canViewInvoiceDetail;
                    const detailAllowed = this.detailAllowedFromNavigation();

                    // Only trigger detail fetch when ACL allows it and no explicit ABAC denial was returned from creation
                    if (canViewDetail && detailAllowed !== false && this.invoiceId()) {
                        if (detailAllowed === true && this.invoiceNumber()) {
                            this.invoiceAccessAllowed.set(true);
                            this.loadingInvoice.set(false);
                        } else {
                            this.fetchInvoiceDetails(this.invoiceId()!);
                        }
                    } else {
                        this.loadingInvoice.set(false);
                        this.invoiceAccessAllowed.set(detailAllowed ?? false);
                    }

                    this.permissionsLoading.set(false);
                },
                error: () => {
                    this.permissionsLoading.set(false);
                },
            });
    }
}
